/**
 * MCP Resource for Flow Templates
 *
 * Exposes flow templates as MCP resources that can be browsed and retrieved.
 */

import type { Resource, ResourceTemplate } from "@modelcontextprotocol/sdk/types.js";
import {
  listTemplates,
  getTemplate,
  getCategories,
  searchTemplates,
  type FlowTemplate,
  type TemplateListItem,
} from "../templates/index.js";

// ============================================================================
// Resource Templates
// ============================================================================

export const templateResourceTemplates: ResourceTemplate[] = [
  {
    uriTemplate: "powerautomate://templates",
    name: "Flow Templates",
    description: "Browse all available flow templates",
    mimeType: "application/json",
  },
  {
    uriTemplate: "powerautomate://templates/{templateId}",
    name: "Flow Template",
    description: "Get a specific flow template by ID",
    mimeType: "application/json",
  },
  {
    uriTemplate: "powerautomate://templates/category/{category}",
    name: "Templates by Category",
    description: "Get templates filtered by category",
    mimeType: "application/json",
  },
  {
    uriTemplate: "powerautomate://templates/search/{query}",
    name: "Search Templates",
    description: "Search templates by keyword",
    mimeType: "application/json",
  },
];

// ============================================================================
// Resource Listing
// ============================================================================

/**
 * List all template resources
 */
export function listTemplateResources(): Resource[] {
  const resources: Resource[] = [];

  // Add main templates list
  resources.push({
    uri: "powerautomate://templates",
    name: "All Flow Templates",
    description: "Browse all available flow templates",
    mimeType: "application/json",
  });

  // Add individual templates
  const templates = listTemplates();
  for (const template of templates) {
    resources.push({
      uri: `powerautomate://templates/${template.id}`,
      name: template.name,
      description: template.description,
      mimeType: "application/json",
    });
  }

  // Add category resources
  const categories = getCategories();
  for (const category of categories) {
    resources.push({
      uri: `powerautomate://templates/category/${encodeURIComponent(category)}`,
      name: `Category: ${category}`,
      description: `Templates in the ${category} category`,
      mimeType: "application/json",
    });
  }

  return resources;
}

// ============================================================================
// Resource Reading
// ============================================================================

interface TemplateResourceContent {
  type: "list" | "template" | "category" | "search";
  data: TemplateListItem[] | FlowTemplate | { categories: string[] };
}

/**
 * Read a template resource
 */
export function readTemplateResource(
  uri: string
): { contents: Array<{ uri: string; mimeType: string; text: string }> } | null {
  const parsed = parseTemplateUri(uri);
  if (!parsed) return null;

  let content: TemplateResourceContent;

  switch (parsed.type) {
    case "list":
      content = {
        type: "list",
        data: listTemplates(),
      };
      break;

    case "template": {
      const template = getTemplate(parsed.id!);
      if (!template) return null;
      content = {
        type: "template",
        data: template,
      };
      break;
    }

    case "category": {
      const templates = listTemplates().filter(
        (t) => t.category.toLowerCase() === parsed.category!.toLowerCase()
      );
      content = {
        type: "category",
        data: templates,
      };
      break;
    }

    case "search": {
      const results = searchTemplates(parsed.query!);
      content = {
        type: "search",
        data: results,
      };
      break;
    }

    case "categories":
      content = {
        type: "list",
        data: { categories: getCategories() },
      };
      break;

    default:
      return null;
  }

  return {
    contents: [
      {
        uri,
        mimeType: "application/json",
        text: JSON.stringify(content.data, null, 2),
      },
    ],
  };
}

// ============================================================================
// URI Parsing
// ============================================================================

interface ParsedTemplateUri {
  type: "list" | "template" | "category" | "search" | "categories";
  id?: string;
  category?: string;
  query?: string;
}

function parseTemplateUri(uri: string): ParsedTemplateUri | null {
  // Remove protocol
  const path = uri.replace("powerautomate://", "");

  // Match patterns
  if (path === "templates" || path === "templates/") {
    return { type: "list" };
  }

  if (path === "templates/categories") {
    return { type: "categories" };
  }

  const categoryMatch = path.match(/^templates\/category\/(.+)$/);
  if (categoryMatch?.[1]) {
    return { type: "category", category: decodeURIComponent(categoryMatch[1]) };
  }

  const searchMatch = path.match(/^templates\/search\/(.+)$/);
  if (searchMatch?.[1]) {
    return { type: "search", query: decodeURIComponent(searchMatch[1]) };
  }

  const templateMatch = path.match(/^templates\/([^/]+)$/);
  if (templateMatch?.[1]) {
    return { type: "template", id: templateMatch[1] };
  }

  return null;
}

/**
 * Check if a URI is a template resource
 */
export function isTemplateResourceUri(uri: string): boolean {
  return uri.startsWith("powerautomate://templates");
}
